import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faArrowRight,
	faCode,
	faEnvelope,
	faPenRuler,
	faRocket,
} from "@fortawesome/free-solid-svg-icons";

import NavBar from "../components/common/navBar";
import Footer from "../components/common/footer";
import ResponsiveProjectLogo from "../components/common/ResponsiveProjectLogo";
import usePageMetadata from "../components/common/usePageMetadata";
import INFO from "../data/user";

import "./styles/homepage.css";

const capabilities = [
	{
		icon: faPenRuler,
		title: "Product thinking",
		description:
			"Discovery, requirements, user flows, and technical scope grounded in the real product problem.",
		accent: "blue",
	},
	{
		icon: faCode,
		title: "Hands-on engineering",
		description:
			"Mobile apps, web software, APIs, data, integrations, and the details that make releases dependable.",
		accent: "violet",
	},
	{
		icon: faRocket,
		title: "Release ownership",
		description:
			"Quality assurance, store and production launches, maintenance, support paths, and iteration after release.",
		accent: "orange",
	},
];

const ProjectLink = ({ project, className, children }) =>
	project.externalUrl ? (
		<a
			className={className}
			href={project.externalUrl}
			target="_blank"
			rel="noopener noreferrer"
		>
			{children}
		</a>
	) : (
		<Link
			className={className}
			to={project.landingPath}
			target="_blank"
			rel="noopener noreferrer"
		>
			{children}
		</Link>
	);

const Homepage = () => {
	usePageMetadata(
		"Lunexa Labs | Independent Product Studio",
		INFO.homepage.description,
	);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	const projectsBySlug = (slugs) =>
		slugs
			.map((slug) =>
				INFO.projects.find((project) => project.slug === slug),
			)
			.filter(Boolean);
	const heroProjects = projectsBySlug([
		"elder-care",
		"life-vault",
		"lunexa-messages",
		"geo-master-quest",
	]);
	const featuredProjects = projectsBySlug([
		"elder-care",
		"life-vault",
		"lunexa-messages",
	]);

	return (
		<div className="portfolio-home">
			<NavBar active="home" />

			<main>
				<section className="portfolio-hero section-shell">
					<div className="portfolio-hero-copy">
						<div className="portfolio-eyebrow">
							{INFO.homepage.eyebrow}
						</div>
						<h1>{INFO.homepage.title}</h1>
						<p>{INFO.homepage.description}</p>
						<div className="portfolio-actions">
							<Link
								className="button button--primary"
								to="/projects"
							>
								Explore the work
								<FontAwesomeIcon icon={faArrowRight} />
							</Link>
							<a
								className="button button--secondary"
								href={`mailto:${INFO.main.email}`}
							>
								<FontAwesomeIcon icon={faEnvelope} />
								Let’s talk
							</a>
						</div>
						<div className="portfolio-availability">
							<span />
							Available for select product collaborations
						</div>
					</div>

					<div className="portfolio-hero-card">
						<div className="hero-card-topline">
							<span>Independent product studio</span>
							<span>Apps · Games · Tools</span>
						</div>
						<h2>
							From a rough idea to a product in people’s hands.
						</h2>
						<div
							className="hero-product-stack"
							aria-label="Selected Lunexa Labs products"
						>
							{heroProjects.map((project) => (
								<ProjectLink
									project={project}
									className={`hero-product-icon hero-product-icon--${project.theme}`}
									key={project.slug}
								>
									<ResponsiveProjectLogo
										project={project}
										alt={project.shortTitle}
										sizes="(max-width: 700px) 64px, 82px"
										fetchPriority="low"
									/>
								</ProjectLink>
							))}
						</div>
						<div className="hero-card-stats">
							<div>
								<strong>7</strong>
								<span>active products</span>
							</div>
							<div>
								<strong>4</strong>
								<span>platforms</span>
							</div>
							<div>
								<strong>1</strong>
								<span>focused studio</span>
							</div>
						</div>
					</div>
				</section>

				<section className="portfolio-work section-shell">
					<div className="portfolio-section-heading">
						<div>
							<span>Selected work</span>
							<h2>Different products. One standard of care.</h2>
						</div>
						<Link to="/projects">
							View every product
							<FontAwesomeIcon icon={faArrowRight} />
						</Link>
					</div>

					<div className="portfolio-work-grid">
						{featuredProjects.map((project) => (
							<ProjectLink
								project={project}
								className={`portfolio-work-card portfolio-work-card--${project.theme}`}
								key={project.slug}
							>
								<div className="work-card-topline">
									<span>{project.category}</span>
									<FontAwesomeIcon icon={faArrowRight} />
								</div>
								<div className="work-card-logo">
									<ResponsiveProjectLogo
										project={project}
										alt=""
										sizes="54px"
										loading="lazy"
									/>
								</div>
								<h3>{project.shortTitle}</h3>
								<p>{project.tagline}</p>
								<small>{project.platform}</small>
							</ProjectLink>
						))}
						<Link
							className="portfolio-work-card portfolio-work-card--more"
							to="/projects"
						>
							<div className="work-card-topline">
								<span>Full portfolio</span>
								<FontAwesomeIcon icon={faArrowRight} />
							</div>
							<div
								className="work-card-more-mark"
								aria-hidden="true"
							>
								+
							</div>
							<h3>View more products</h3>
							<p>
								Explore games, privacy tools, communication
								apps, and focused utilities.
							</p>
							<small>See all 7 products</small>
						</Link>
					</div>
				</section>

				<section className="portfolio-capabilities section-shell">
					<div className="portfolio-section-heading">
						<div>
							<span>Software development lifecycle</span>
							<h2>
								One product partner from discovery to support.
							</h2>
						</div>
						<Link to="/software-development-lifecycle">
							Explore the process
							<FontAwesomeIcon icon={faArrowRight} />
						</Link>
					</div>
					<div className="capability-grid">
						{capabilities.map((item) => (
							<article
								className={`capability-card capability-card--${item.accent}`}
								key={item.title}
							>
								<div>
									<FontAwesomeIcon icon={item.icon} />
								</div>
								<h3>{item.title}</h3>
								<p>{item.description}</p>
							</article>
						))}
					</div>
				</section>

				<section className="portfolio-principle section-shell">
					<div className="principle-mark">✓</div>
					<div>
						<span>Product standard</span>
						<h2>
							Good software should feel obvious after someone has
							done the hard thinking.
						</h2>
					</div>
					<Link to="/about">
						How Lunexa Labs works
						<FontAwesomeIcon icon={faArrowRight} />
					</Link>
				</section>

				<section className="portfolio-contact section-shell">
					<span>Have a product worth doing properly?</span>
					<h2>Let’s make the next release feel inevitable.</h2>
					<a href={`mailto:${INFO.main.email}`}>{INFO.main.email}</a>
				</section>
			</main>

			<Footer />
		</div>
	);
};

export default Homepage;
